import { CONSENT_REQUEST_STATUS } from "@core/constants/source"
import axios from "@core/plugins/axios.plugin"
import { ServiceError } from "@core/exceptions"
import { handleServiceCall } from "@services/helpers"
import type { ConsentRequest } from "@types"
import type { Address } from "@types"
import type { ConsentRequestInvitationDto } from "@core/types/source/consent_request"

// =====================
// Type definitions des réponses des services
// =====================

export type ConsentRequestsResponse = ConsentRequest[]

/** Payload pour createConsentRequestSign (titulaire = owner) */
export type CreateConsentRequestSignPayload = {
    titulaire: {
        user: {
            _id?: string
            firstname: string
            lastname: string
            username: string
            isPro?: boolean
        }
        company: {
            _id?: string
            name: string
            type?: string
            address?: Address | null
        } | null
    }
    source: { idsource: string; idtype: string }
    address: Address
    buildingId: string
    dateFromData?: string | null
    isPro?: boolean
}

/** Réponse typée sign / extend / change-owner (champs utilisés par SourceConsentSign) */
export type ConsentRequestSignResponse = {
    _id?: string
    /** URL complète d’invitation (champ `url` après populateAndFormat métier) */
    url?: string
    consentement?: {
        _id?: string
        date_to?: string
        date_from?: string
        date_from_data?: string | null
        signed_at?: string
        updatedAt?: string
        dismissed?: boolean
        dismissed_at?: string | null
        signed?: boolean
        createdAt?: string
    }
    source?: { _id?: string }
    token?: string
    task_building?: unknown
    /** Présent sur PUT change-owner (déclaration GRDF) */
    task?: { event_name?: string } & Record<string, unknown>
}

/** Corps POST /v5/consentrequest/change-owner (validateur métier : _id_user, branches user/pro). */
export type CreateConsentRequestChangeOwnerCompany = {
    name: string
    type: string
} & Record<string, unknown>

export type CreateConsentRequestChangeOwnerBody = {
    source: {
        _id_source: string
        date_from_data?: string
    }
    user: {
        _id_user?: string
        firstname?: string
        lastname?: string
        username?: string
        company?: CreateConsentRequestChangeOwnerCompany
    }
    mail?: boolean
}

/** États renvoyés par l’API en erreur HTTP mais avec corps exploitable (comportement aligné legacy). */
const INVITATION_TERMINAL_STATES: string[] = [
    CONSENT_REQUEST_STATUS.TERMINATED,
    CONSENT_REQUEST_STATUS.EXPIRED,
    CONSENT_REQUEST_STATUS.REFUSED,
    CONSENT_REQUEST_STATUS.REMOVED,
]

function throwAxiosAsServiceError(err: unknown): never {
    const error = err as {
        response?: {
            status?: number
            data?: {
                message?: string
                code?: string
                detail?: { message?: string; code?: string; details?: unknown }
                details?: unknown
            }
        }
        message?: string
    }
    if (error.response) {
        const d = error.response.data
        throw new ServiceError(
            {
                message:
                    d?.detail?.message ??
                    d?.message ??
                    "Une erreur est survenue",
                code: d?.detail?.code ?? d?.code ?? "unknown",
                details: d?.detail?.details ?? d?.details,
                description: error.message,
            },
            error.response.status ?? 500
        )
    }
    throw new ServiceError(
        {
            message: "Une erreur inconnue est survenue",
            code: "UNKNOWN",
            description: (err as Error)?.message,
        },
        500
    )
}
/**
 * Entreprise destinataire (titulaire) pour POST consentrequestmulti
 */
export type CreateConsentRequestMultiCompanyPayload = {
    name: string
    address: Address | null
    type: string
    idcompany?: string
}

/**
 * Une ligne de compteur dans le corps d'appel (sérialisée sous consentrequest côté API)
 */
export type CreateConsentRequestMultiConsentItem = {
    address: Address
    idtype: "pdl" | "pce"
    idsource: string
    date_from_data?: string | null
    _id_building: string
}

/**
 * Paramètres de {@link ConsentRequestService.createConsentRequestMulti}
 */
export type CreateConsentRequestMultiParams = {
    user: {
        firstname: string
        lastname: string
        username: string
    }
    company?: CreateConsentRequestMultiCompanyPayload | null
    consentRequests: CreateConsentRequestMultiConsentItem[]
}

/**
 * Réponse minimale consommée par le parcours multi (création puis machine / récap)
 */
export type CreateConsentRequestMultiResponse = {
    _id: string
    url?: string
    invalidConsentRequests?: Array<{ idsource: string; idtype: string }>
}
// =====================
// Services
// =====================
export class ConsentRequestService {
    /**
     * GET /v5/consentrequest
     * Récupère les demandes de signature de consentement
     *
     * @return Les demande de signature de consentements
     */
    async getConsentRequests(): Promise<ConsentRequestsResponse> {
        return handleServiceCall<ConsentRequestsResponse>(async () => {
            const { data } = await axios.get("/api/metier/v5/consentrequest", {
                params: {
                    noPage: true,
                },
            })
            return data
        })
    }

    /**
     * GET /v5/consentrequest/sign/:id
     * Récupère une demande SIGN (lien e-mail). Si l’API renvoie un état terminal dans le corps d’erreur, retourne `{ state }` (legacy).
     */
    async getConsentRequestSign(
        requestId: string,
        token?: string | null
    ): Promise<ConsentRequestInvitationDto> {
        try {
            const { data } = await axios.get(
                `/api/metier/v5/consentrequest/sign/${requestId}`,
                {
                    params:
                        token != null && token !== "" ? { token } : undefined,
                }
            )
            return data as ConsentRequestInvitationDto
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { state?: string } } }
            if (!ax.response) {
                throwAxiosAsServiceError(err)
            }
            const state = ax.response.data?.state
            if (state && INVITATION_TERMINAL_STATES.includes(state)) {
                return { state }
            }
            throwAxiosAsServiceError(err)
        }
    }

    /**
     * GET /v5/consentrequest/share/:id
     * Récupère une demande SHARE (lien e-mail). Même logique d’états terminaux que {@link getConsentRequestSign}.
     */
    async getConsentRequestShare(
        requestId: string,
        token?: string | null
    ): Promise<ConsentRequestInvitationDto> {
        try {
            const { data } = await axios.get(
                `/api/metier/v5/consentrequest/share/${requestId}`,
                {
                    params:
                        token != null && token !== "" ? { token } : undefined,
                }
            )
            return data as ConsentRequestInvitationDto
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { state?: string } } }
            if (!ax.response) {
                throwAxiosAsServiceError(err)
            }
            const state = ax.response.data?.state
            if (state && INVITATION_TERMINAL_STATES.includes(state)) {
                return { state }
            }
            throwAxiosAsServiceError(err)
        }
    }

    /**
     * GET /v5/consentrequest/extend/:id
     * Récupère une demande EXTEND (lien e-mail). Même logique d'états terminaux que {@link getConsentRequestSign}.
     */
    async getConsentRequestExtend(
        requestId: string,
        token?: string | null
    ): Promise<ConsentRequestInvitationDto> {
        try {
            const { data } = await axios.get(
                `/api/metier/v5/consentrequest/extend/${requestId}`,
                {
                    params:
                        token != null && token !== "" ? { token } : undefined,
                }
            )
            return data as ConsentRequestInvitationDto
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { state?: string } } }
            if (!ax.response) {
                throwAxiosAsServiceError(err)
            }
            const state = ax.response.data?.state
            if (state && INVITATION_TERMINAL_STATES.includes(state)) {
                return { state }
            }
            throwAxiosAsServiceError(err)
        }
    }

    /**
     * GET /v5/consentrequestmulti/:id
     * Demande multi-compteurs (détail formaté). Query `token` optionnelle (tracking des vues).
     */
    async getConsentRequestMulti(
        requestMultiId: string,
        token?: string | null
    ): Promise<Record<string, unknown>> {
        return handleServiceCall<Record<string, unknown>>(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/consentrequestmulti/${requestMultiId}`,
                {
                    params:
                        token != null && token !== "" ? { token } : undefined,
                }
            )
            return data as Record<string, unknown>
        })
    }

    /**
     * PUT /api/metier/v5/consentrequestmulti/:_id_consentrequestmulti
     * Passe une demande de signature de consentement multi à l'état ACCEPTED (signature validée).
     *
     * @param _id_consentrequestmulti - Identifiant unique de la demande multi
     * @returns Données de la demande mise à jour
     */
    async updateConsentRequestMultiById(
        _id_consentrequestmulti: string
    ): Promise<unknown> {
        return handleServiceCall<unknown>(async () => {
            const { data } = await axios.put(
                `/api/metier/v5/consentrequestmulti/${_id_consentrequestmulti}`
            )
            return data
        })
    }

    /**
     * GET /v5/consentrequest/change-owner/:id
     * Demande CHANGE_OWNER (lien e-mail). Pas de query `token` côté API.
     */
    async getConsentRequestChangeOwner(
        requestId: string
    ): Promise<ConsentRequestInvitationDto> {
        try {
            const { data } = await axios.get(
                `/api/metier/v5/consentrequest/change-owner/${requestId}`
            )
            return data as ConsentRequestInvitationDto
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { state?: string } } }
            if (!ax.response) {
                throwAxiosAsServiceError(err)
            }
            const state = ax.response.data?.state
            if (state && INVITATION_TERMINAL_STATES.includes(state)) {
                return { state }
            }
            throwAxiosAsServiceError(err)
        }
    }

    /**
     * PUT /v5/consentrequest/share/:id
     * Met à jour le statut d’une demande de partage (TERMINATED = accepté, REFUSED, etc.).
     * `disable_task` : aligné sur le body métier ; false par défaut (l’API peut lancer `building.onboarding.user`).
     */
    async updateConsentRequestShare(
        requestId: string,
        status: string = CONSENT_REQUEST_STATUS.TERMINATED,
        options: { mail?: boolean; disable_task?: boolean } = {}
    ): Promise<ConsentRequestSignResponse> {
        const { mail = true, disable_task = false } = options
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const { data } = await axios.put(
                `/api/metier/v5/consentrequest/share/${requestId}`,
                { status, mail, disable_task }
            )
            return data
        })
    }

    /**
     * PUT /v5/consentrequest/:id/declaration-information
     * Met à jour date d’ouverture contrat et code postal pour une DDA PCE (invitation).
     */
    async updateConsentRequestPCE(payload: {
        requestId: string
        date: string
        zipcode: string
    }): Promise<unknown> {
        return handleServiceCall(async () => {
            const { data } = await axios.put(
                `/api/metier/v5/consentrequest/${payload.requestId}/declaration-information`,
                {
                    date_from_data: payload.date,
                    zipcode: payload.zipcode,
                }
            )
            return data
        })
    }

    /**
     * Supprime une demande de consentement en fonction de son type
     * @param _id_consentrequest - Identifiant de la demande de consentement
     * @param type - Type de la demande de consentement (sign ou share)
     */
    async deleteConsentRequest(
        _id_consentrequest: string,
        type: "sign" | "share" | "extend" | "change-owner" = "sign"
    ) {
        return handleServiceCall<void>(async () => {
            await axios.delete(
                `/api/metier/v5/consentrequest/${type}/${_id_consentrequest}`
            )
        })
    }

    /**
     * POST /v5/consentrequest/sign
     * Crée une demande de signature de consentement pour un compteur (PDL ou PCE).
     */
    async createConsentRequestSign(
        payload: CreateConsentRequestSignPayload
    ): Promise<ConsentRequestSignResponse> {
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const { data } = await axios.post(
                "/api/metier/v5/consentrequest/sign",
                {
                    user: {
                        ...payload.titulaire.user,
                        isPro:
                            payload.isPro ?? !!payload.titulaire.company?.name,
                    },
                    company: payload.titulaire.company,
                    source: {
                        idsource: payload.source.idsource,
                        idtype: payload.source.idtype,
                    },
                    date_from_data: payload.dateFromData ?? null,
                    address: payload.address,
                    building: { _id: payload.buildingId },
                }
            )
            return data
        })
    }

    /**
     * PUT /v5/consentrequest/sign/:_id_consentrequest
     * Passe une demande de signature en état TERMINATED (ou REFUSED).
     */
    async updateConsentRequestSign(
        requestId: string,
        options: {
            status?: string
            mail?: boolean
            disable_task?: boolean
        } = {}
    ): Promise<ConsentRequestSignResponse> {
        const {
            status = CONSENT_REQUEST_STATUS.TERMINATED,
            mail = true,
            disable_task = false,
        } = options
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const { data } = await axios.put(
                `/api/metier/v5/consentrequest/sign/${requestId}`,
                { status, mail, disable_task }
            )
            return data
        })
    }

    /**
     * POST /v5/consentrequest/extend
     * Crée une demande de prolongement de consentement.
     */
    async createConsentRequestExtend(
        sourceId: string,
        userId: string,
        options: { mail?: boolean; signal?: AbortSignal } = {}
    ): Promise<ConsentRequestSignResponse> {
        const { mail = false, signal } = options
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const { data } = await axios.post(
                "/api/metier/v5/consentrequest/extend",
                { _id_source: sourceId, _id_user_to: userId, mail },
                { signal }
            )
            return data
        })
    }

    /**
     * PUT /v5/consentrequest/extend/:id
     * Met à jour une demande de prolongement (ex. TERMINATED).
     */
    async updateConsentRequestExtend(
        consentRequestId: string,
        status: string = CONSENT_REQUEST_STATUS.TERMINATED,
        options: { mail?: boolean } = {}
    ): Promise<ConsentRequestSignResponse> {
        const { mail = false } = options
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const { data } = await axios.put(
                `/api/metier/v5/consentrequest/extend/${consentRequestId}`,
                { status, mail }
            )
            return data
        })
    }

    /**
     * PUT /v5/consentrequest/change-owner/:id
     * Met à jour une demande de changement de titulaire (ex. TERMINATED).
     */
    async updateConsentRequestChangeOwner(
        consentRequestId: string,
        status:
            | typeof CONSENT_REQUEST_STATUS.TERMINATED
            | typeof CONSENT_REQUEST_STATUS.REFUSED = CONSENT_REQUEST_STATUS.TERMINATED,
        options: { mail?: boolean } = {}
    ): Promise<ConsentRequestSignResponse> {
        const { mail = false } = options
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const { data } = await axios.put(
                `/api/metier/v5/consentrequest/change-owner/${consentRequestId}`,
                { status, mail }
            )
            return data
        })
    }

    /**
     * POST /v5/consentrequest/change-owner
     * Crée une demande de changement de titulaire.
     */
    async createConsentRequestChangeOwner(
        body: CreateConsentRequestChangeOwnerBody,
        options: { signal?: AbortSignal } = {}
    ): Promise<ConsentRequestSignResponse> {
        const { signal } = options
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const user = { ...body.user }
            if (!user._id_user?.trim()) {
                delete user._id_user
            }
            const payload: CreateConsentRequestChangeOwnerBody = {
                ...body,
                user,
            }
            const { data } = await axios.post(
                "/api/metier/v5/consentrequest/change-owner",
                payload,
                { signal }
            )
            return data
        })
    }

    /**
     * POST /v5/consentrequest/share
     * Crée une demande de partage (SHARE) pour un compteur existant.
     * @param payload - sourceId (id MongoDB de la source), buildingId (id MongoDB du bâtiment)
     * @returns La demande de partage créée (format proche ConsentRequestSignResponse)
     */
    async createConsentRequestShare(payload: {
        sourceId: string
        buildingId: string
        mail?: boolean
    }): Promise<ConsentRequestSignResponse> {
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const { data } = await axios.post(
                "/api/metier/v5/consentrequest/share",
                {
                    _id_source: payload.sourceId,
                    _id_building: payload.buildingId,
                    mail: payload.mail ?? false,
                }
            )
            return data
        })
    }

    /**
     * POST /v5/mail/consentrequest/:_id_consentrequest
     * Envoie un mail d'invitation ou de relance pour une demande de consentement.
     * @param requestId - Identifiant de la demande de consentement
     * @param sendBack - Si true, envoie une relance ; sinon envoie l'invitation
     */
    async sendConsentRequest(
        requestId: string,
        sendBack: boolean = false
    ): Promise<ConsentRequestSignResponse> {
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const { data } = await axios.post(
                `/api/metier/v5/mail/consentrequest/${requestId}`,
                { follow_up: true },
                {
                    params: sendBack ? { type: "relance" } : {},
                }
            )
            return data
        })
    }

    /**
     * GET /v5/mail/consentrequest/:_id_consentrequest/preview
     * Récupère la prévisualisation HTML du mail d'invitation.
     * @param requestId - Identifiant de la demande de consentement
     * @returns HTML du mail (string)
     */
    async previewConsentRequest(requestId: string): Promise<string> {
        return handleServiceCall<string>(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/mail/consentrequest/${requestId}/preview`,
                { responseType: "text" }
            )
            return data
        })
    }

    /**
     * POST /v5/mail/consentrequestmulti/:_id_consentrequestmulti
     * Envoie un mail d'invitation ou de relance pour une demande multi-compteurs.
     */
    async sendConsentRequestMulti(
        requestId: string,
        sendBack: boolean = false
    ): Promise<ConsentRequestSignResponse> {
        return handleServiceCall<ConsentRequestSignResponse>(async () => {
            const { data } = await axios.post(
                `/api/metier/v5/mail/consentrequestmulti/${requestId}`,
                { follow_up: true },
                {
                    params: sendBack ? { type: "relance" } : {},
                }
            )
            return data
        })
    }

    /**
     * GET /v5/mail/consentrequestmulti/:_id_consentrequestmulti/preview
     * Récupère la prévisualisation HTML du mail pour une demande multi-compteurs.
     */
    async previewConsentRequestMulti(requestId: string): Promise<string> {
        return handleServiceCall<string>(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/mail/consentrequestmulti/${requestId}/preview`,
                { responseType: "text" }
            )
            return data
        })
    }

    /**
     * POST /api/metier/v5/consentrequestmulti
     * Crée une demande de signature de consentement multi
     * @param user - Informations de l'utilisateur recevant la demande
     * @param company - Informations de l'entreprise recevant la demande (optionnel)
     * @param consentRequests - Liste des demandes de signature de consentement
     * @returns Les informations de la demande créée
     */
    async createConsentRequestMulti({
        user,
        company,
        consentRequests,
    }: CreateConsentRequestMultiParams): Promise<CreateConsentRequestMultiResponse> {
        return handleServiceCall<CreateConsentRequestMultiResponse>(
            async () => {
                const { data } = await axios.post(
                    "/api/metier/v5/consentrequestmulti",
                    {
                        user,
                        company,
                        consentrequest: consentRequests,
                    }
                )
                return data
            }
        )
    }
}
